import { createContext, ReactNode, useEffect, useState } from "react";
import { CallBackProps, Step, EVENTS } from "react-joyride";

import { useJoyrideSteps } from "./joyrideHooks";
import { useDemoStudyArea } from "hooks/studyAreaHooks";
import { useNavigate } from "hooks/routerHooks";
import { useMatch } from "react-router-dom";

interface JoyrideContextValues {
  runJoyride: boolean;
  toggleJoyride: (value: boolean) => void;
  activateJoyride: (value: boolean) => void;
  callbackFunction: (data: CallBackProps) => void;
  setNextIndex: (value: number) => void;
  isJoyrideActive: boolean;
  steps: Step[];
  stepIndex: number;
}

export const JoyrideContext = createContext<JoyrideContextValues>({
  runJoyride: false,
  isJoyrideActive: false,
  toggleJoyride: (value: boolean) => {},
  activateJoyride: (value: boolean) => {},
  callbackFunction: (data: CallBackProps) => {},
  setNextIndex: (value: number) => {},
  steps: [],
  stepIndex: 0,
});

interface Props {
  children: ReactNode;
}

export function JoyrideContextProvider(props: Props) {
  const { children } = props;

  const steps = useJoyrideSteps();
  const navigate = useNavigate();

  const { data: demoStudyArea } = useDemoStudyArea();
  const [isJoyrideActive, setIsJoyrideActive] = useState<boolean>(false);
  const [runJoyride, setRunJoyride] = useState<boolean>(false);
  const [stepIndex, setStepIndex] = useState<number>(0);
  const matchedLocation = useMatch(`/${demoStudyArea?.uuid}`);

  useEffect(() => {
    if (isJoyrideActive && !matchedLocation && stepIndex === 0) {
      navigate(`/${demoStudyArea?.uuid}`);
    }
  }, [
    stepIndex,
    matchedLocation,
    navigate,
    demoStudyArea?.uuid,
    isJoyrideActive,
  ]);

  function activateJoyride(value: boolean) {
    setRunJoyride(value);
    setIsJoyrideActive(value);
  }

  function callbackFunction(data: CallBackProps) {
    if (data.type === EVENTS.TOUR_END || data.action === "close") {
      activateJoyride(false);

      return window.close();
    }
    if (
      ([EVENTS.STEP_AFTER, EVENTS.TARGET_NOT_FOUND] as string[]).includes(
        data.type
      )
    ) {
      const nextIndex = stepIndex + (data.action === "prev" ? -1 : 1);

      if (data.index === 0 && data.action === "next") {
        navigate(`/${demoStudyArea?.uuid}/climate-effects`);
        setRunJoyride(false);
      }

      if (data.index === 1 && data.action === "next") {
        navigate(`/${demoStudyArea?.uuid}/measures`);
      }

      if (data.index === 1 && data.action === "prev") {
        navigate(`/${demoStudyArea?.uuid}`);
      }

      if (data.index === 2 && data.action === "prev") {
        navigate(`/${demoStudyArea?.uuid}/climate-effects`);
        setRunJoyride(false);
      }

      if (data.index === 2 && data.action === "next") {
        navigate(`/${demoStudyArea?.uuid}/result`);
        setRunJoyride(false);
      }
      if (data.index === 3 && data.action === "prev") {
        navigate(`/${demoStudyArea?.uuid}/measures`);
        setRunJoyride(false);
      }

      setNextIndex(nextIndex);
    }
  }

  function setNextIndex(value: number) {
    setStepIndex(value);
  }

  function toggleJoyride(value: boolean) {
    setRunJoyride(value);
  }

  return (
    <JoyrideContext.Provider
      value={{
        runJoyride,
        steps,
        isJoyrideActive,
        stepIndex,
        activateJoyride,
        setNextIndex,
        callbackFunction,
        toggleJoyride,
      }}
    >
      {children}
    </JoyrideContext.Provider>
  );
}
